/**
 * Customer Address City Dropdown
 * Dynamically populates city dropdown based on selected province/state
 */

(function() {
  'use strict';

  // Get city list from theme settings
  const cityList = window.theme?.cityList || {};

  // Map province names to their city list keys
  // This handles variations in province naming
  const provinceToCities = {
    // Abu Dhabi variations
    'Abu Dhabi': cityList.abudhabi || [],
    'Abu dhabi': cityList.abudhabi || [],
    'abu dhabi': cityList.abudhabi || [],
    'AbuDhabi': cityList.abudhabi || [],
    'أبوظبي': cityList.abudhabi || [],
    
    // Ajman variations
    'Ajman': cityList.ajman || [],
    'ajman': cityList.ajman || [],
    'عجمان': cityList.ajman || [],
    
    // Dubai variations
    'Dubai': cityList.dubai || [],
    'dubai': cityList.dubai || [],
    'دبي': cityList.dubai || [],
    
    // Fujairah variations
    'Fujairah': cityList.fujairah || [],
    'fujairah': cityList.fujairah || [],
    'Al Fujairah': cityList.fujairah || [],
    'الفجيرة': cityList.fujairah || [],
    
    // Ras Al Khaimah variations
    'Ras Al Khaimah': cityList.rasalkhaimah || [],
    'Ras al Khaimah': cityList.rasalkhaimah || [],
    'Ras al-Khaimah': cityList.rasalkhaimah || [],
    'RAK': cityList.rasalkhaimah || [],
    'رأس الخيمة': cityList.rasalkhaimah || [],
    
    // Sharjah variations
    'Sharjah': cityList.sharjah || [],
    'sharjah': cityList.sharjah || [],
    'الشارقة': cityList.sharjah || [],
    
    // Umm Al Quwain variations
    'Umm Al Quwain': cityList.ummalquwain || [],
    'Umm al Quwain': cityList.ummalquwain || [],
    'Umm al-Quwain': cityList.ummalquwain || [],
    'UAQ': cityList.ummalquwain || [],
    'أم القيوين': cityList.ummalquwain || []
  };

  /**
   * Get cities for a given province name
   * @param {string} provinceName - The name of the province
   * @returns {Array} Array of city names
   */
  function getCitiesForProvince(provinceName) {
    if (!provinceName) return [];
    
    // Direct match
    if (provinceToCities[provinceName]) {
      return provinceToCities[provinceName];
    }
    
    // Case-insensitive match
    const lowerProvince = provinceName.toLowerCase().trim();
    for (const [key, cities] of Object.entries(provinceToCities)) {
      if (key.toLowerCase() === lowerProvince) {
        return cities;
      }
    }
    
    // Partial match (for variations like "Abu Dhabi Emirate")
    for (const [key, cities] of Object.entries(provinceToCities)) {
      if (lowerProvince.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerProvince)) {
        return cities;
      }
    }
    
    return [];
  }

  /**
   * Convert city input to select dropdown
   * @param {HTMLInputElement} cityInput - The city input element
   * @param {Array} cities - Array of city names
   * @param {string} currentValue - Current city value to preserve
   */
  function convertToSelect(cityInput, cities, currentValue) {
    // Create select element
    const select = document.createElement('select');
    
    // Copy attributes from input
    select.id = cityInput.id;
    select.name = cityInput.name;
    select.className = cityInput.className;
    select.setAttribute('autocomplete', cityInput.getAttribute('autocomplete') || 'address-level2');
    
    if (cityInput.hasAttribute('required')) {
      select.setAttribute('required', '');
    }
    
    // Add data attribute to identify this as a dynamic city select
    select.setAttribute('data-city-select', 'true');
    
    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Select City';
    select.appendChild(defaultOption);
    
    // Add city options
    cities.forEach(city => {
      const trimmedCity = city.trim();
      if (trimmedCity) {
        const option = document.createElement('option');
        option.value = trimmedCity;
        option.textContent = trimmedCity;
        
        // Pre-select if matches current value
        if (currentValue && trimmedCity.toLowerCase() === currentValue.toLowerCase()) {
          option.selected = true;
        }
        
        select.appendChild(option);
      }
    });
    
    // Replace input with select
    cityInput.parentNode.replaceChild(select, cityInput);
    
    return select;
  }

  /**
   * Convert city select back to text input
   * @param {HTMLSelectElement} citySelect - The city select element
   * @param {string} currentValue - Current city value to preserve
   */
  function convertToInput(citySelect, currentValue) {
    // Create input element
    const input = document.createElement('input');
    input.type = 'text';
    input.id = citySelect.id;
    input.name = citySelect.name;
    input.className = citySelect.className;
    input.value = currentValue || '';
    input.setAttribute('autocomplete', citySelect.getAttribute('autocomplete') || 'address-level2');
    
    if (citySelect.hasAttribute('required')) {
      input.setAttribute('required', '');
    }
    
    // Replace select with input
    citySelect.parentNode.replaceChild(input, citySelect);
    
    return input;
  }

  /**
   * Handle province change event
   * @param {Event} event - The change event
   */
  function handleProvinceChange(event) {
    const provinceSelect = event.target;
    const selectedProvince = provinceSelect.value;
    
    // Find the corresponding city field
    // The city input/select should be in the same form
    const form = provinceSelect.closest('form');
    if (!form) return;
    
    const cityField = form.querySelector('[name="address[city]"]');
    if (!cityField) return;
    
    const cities = getCitiesForProvince(selectedProvince);
    const currentValue = cityField.value;
    
    if (cities.length > 0) {
      // Convert to select if not already
      if (cityField.tagName.toLowerCase() === 'input') {
        convertToSelect(cityField, cities, currentValue);
      } else if (cityField.tagName.toLowerCase() === 'select') {
        // Update existing select options
        const isDataCitySelect = cityField.hasAttribute('data-city-select');
        if (isDataCitySelect) {
          // Clear existing options except the default
          while (cityField.options.length > 1) {
            cityField.remove(1);
          }
          
          // Add new city options
          cities.forEach(city => {
            const trimmedCity = city.trim();
            if (trimmedCity) {
              const option = document.createElement('option');
              option.value = trimmedCity;
              option.textContent = trimmedCity;
              
              if (currentValue && trimmedCity.toLowerCase() === currentValue.toLowerCase()) {
                option.selected = true;
              }
              
              cityField.appendChild(option);
            }
          });
        } else {
          // It's a regular select, convert it
          convertToSelect(cityField, cities, currentValue);
        }
      }
    } else {
      // No cities for this province, convert back to input if it's a select
      if (cityField.tagName.toLowerCase() === 'select' && cityField.hasAttribute('data-city-select')) {
        convertToInput(cityField, currentValue);
      }
    }
  }

  /**
   * Initialize city dropdown for a province select
   * @param {HTMLSelectElement} provinceSelect - The province select element
   */
  function initializeProvinceSelect(provinceSelect) {
    // Add change event listener
    provinceSelect.addEventListener('change', handleProvinceChange);
    
    // Check if province is already selected (editing existing address)
    const selectedProvince = provinceSelect.value;
    if (selectedProvince) {
      // Trigger the change handler to populate cities
      handleProvinceChange({ target: provinceSelect });
    }
  }

  /**
   * Initialize all province selects on the page
   */
  function initializeAll() {
    // Find all province select elements
    const provinceSelects = document.querySelectorAll('select[name="address[province]"]');
    
    provinceSelects.forEach(provinceSelect => {
      initializeProvinceSelect(provinceSelect);
    });
  }

  /**
   * Re-initialize when new address forms are added (e.g., modals)
   */
  function observeNewForms() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node contains province selects
            const provinceSelects = node.querySelectorAll ? 
              node.querySelectorAll('select[name="address[province]"]') : [];
            
            provinceSelects.forEach(provinceSelect => {
              // Only initialize if not already initialized
              if (!provinceSelect.hasAttribute('data-city-initialized')) {
                provinceSelect.setAttribute('data-city-initialized', 'true');
                initializeProvinceSelect(provinceSelect);
              }
            });
            
            // Also check if the node itself is a province select
            if (node.matches && node.matches('select[name="address[province]"]')) {
              if (!node.hasAttribute('data-city-initialized')) {
                node.setAttribute('data-city-initialized', 'true');
                initializeProvinceSelect(node);
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAll();
      observeNewForms();
    });
  } else {
    initializeAll();
    observeNewForms();
  }

  // Also listen for Shopify's CountryProvinceSelector updates
  // This handles cases where provinces are populated after country selection
  document.addEventListener('change', (event) => {
    if (event.target.matches('select[name="address[country]"]')) {
      // Wait for Shopify's CountryProvinceSelector to update provinces
      setTimeout(() => {
        const form = event.target.closest('form');
        if (form) {
          const provinceSelect = form.querySelector('select[name="address[province]"]');
          if (provinceSelect && !provinceSelect.hasAttribute('data-city-initialized')) {
            provinceSelect.setAttribute('data-city-initialized', 'true');
            initializeProvinceSelect(provinceSelect);
          }
        }
      }, 100);
    }
  });

})();
